import {
  get,
  set,
  setMany,
  createStore,
  type UseStore,
  entries,
  clear,
  del,
  delMany,
} from 'idb-keyval';

import { aPersonCanUseWhiteboards } from '@canvas/capability-summaries';
import {
  updateIndexeddbStatus,
  withTimeout,
  indexeddbStatus,
} from '@canvas/indexeddb-state';
import {
  captureException,
  CapabilityTier,
  CustomerImpact,
  captureExceptionForCapability,
} from '@canvas/sentry-react';

// bumped from 3000ms
const INDEXEDDB_TIMEOUT_MS = 30_000;

const withIndexeddbTimeout = async <T>(operation: () => Promise<T>, fallback?: T) => {
  if (!indexeddbStatus.healthy) {
    return fallback as T;
  }

  const configured = withTimeout<T>(INDEXEDDB_TIMEOUT_MS, (error) => {
    updateIndexeddbStatus(false);
    // We also want to directly report this to sentry since we will be letting the other indexedDB
    // promise hang
    captureException(error, {
      tags: {
        captureLocation: 'withIndexeddbTimeout-timeout',
        capabilityTier: CapabilityTier.C2,
        customerImpact: CustomerImpact.CUSTOMERS,
        method: operation.name,
      },
    });
  });

  return configured(operation);
};

type GetParams = Parameters<typeof get>;
type GetOptions<T> = {
  fallback?: T;
  customStore?: GetParams[1];
  /** Report any errors in real-time error tracking system  */
  reportError?: boolean;
};
export async function safeGet<T = any>(
  key: GetParams[0],
  { fallback, customStore, reportError = true }: GetOptions<T> = {},
): Promise<T | undefined> {
  try {
    return await withIndexeddbTimeout(() => get(key, customStore), fallback);
  } catch (error) {
    if (reportError) {
      captureExceptionForCapability(
        error,
        {
          tags: {
            captureLocation: 'safeGet-catch-all',
            /**
             * It's hard to reason about which capabilities will actually be affected in this
             * circumstance so we'll assume C2
             */
            capabilityTier: CapabilityTier.C2,
            customerImpact: CustomerImpact.CUSTOMERS,
          },
        },
        aPersonCanUseWhiteboards,
      );
    }
    return fallback;
  }
}

type EntriesParams = Parameters<typeof entries>;
export async function safeEntries<T = any>(
  ...args: EntriesParams
): Promise<[IDBValidKey, T][] | undefined> {
  try {
    return await withIndexeddbTimeout(() => entries(...args));
  } catch (error) {
    captureExceptionForCapability(
      error,
      {
        tags: {
          captureLocation: 'safeEntries-catch-all',
          capabilityTier: CapabilityTier.C2,
          customerImpact: CustomerImpact.CUSTOMERS,
        },
      },
      /**
       * It's hard to reason about which capabilities will actually be affected in this
       * circumstance but we'll assume being unable to
       * retrieve data may result in the board not being viewable (for which there's an SLA)
       * @see {@link https://www.atlassian.com/legal/sla#appendix-a}
       */
      aPersonCanUseWhiteboards,
    );
  }
}

type SetParams = Parameters<typeof set>;
export async function safeSet<T = any>(
  key: SetParams[0],
  value: T,
  customStore?: SetParams[2],
): ReturnType<typeof set> {
  try {
    return await withIndexeddbTimeout(() => set(key, value, customStore));
  } catch (error) {
    captureException(error, {
      tags: {
        captureLocation: 'safeSet-catch-all',
        /**
         * It's hard to reason about which capabilities will actually be affected in this
         * circumstance so we'll assume C2
         */
        capabilityTier: CapabilityTier.C2,
        customerImpact: CustomerImpact.CUSTOMERS,
      },
    });
    return;
  }
}

type DeleteParams = Parameters<typeof del>;
export async function safeDelete(key: DeleteParams[0], customStore?: DeleteParams[1]) {
  try {
    return await withIndexeddbTimeout(() => del(key, customStore));
  } catch (error) {
    captureException(error, {
      tags: {
        captureLocation: 'safeDelete-catch-all',
        /**
         * It's hard to reason about which capabilities will actually be affected in this
         * circumstance so we'll assume C2
         */
        capabilityTier: CapabilityTier.C2,
        customerImpact: CustomerImpact.CUSTOMERS,
      },
    });
    return;
  }
}

type DeleteManyParams = Parameters<typeof delMany>;
export async function safeDeleteMany(
  entries: DeleteManyParams[0],
  customStore?: DeleteManyParams[1],
) {
  try {
    return await withIndexeddbTimeout(() => delMany(entries, customStore));
  } catch (error) {
    captureException(error, {
      tags: {
        captureLocation: 'safeDeleteMany-catch-all',
        /**
         * It's hard to reason about which capabilities will actually be affected in this
         * circumstance so we'll assume C2
         */
        capabilityTier: CapabilityTier.C2,
        customerImpact: CustomerImpact.CUSTOMERS,
      },
    });
    return;
  }
}

type SetManyParams = Parameters<typeof setMany>;
export async function safeSetMany(
  entries: SetManyParams[0],
  customStore?: SetManyParams[1],
): ReturnType<typeof setMany> {
  try {
    return await withIndexeddbTimeout(() => setMany(entries, customStore));
  } catch (error) {
    captureException(error, {
      tags: {
        captureLocation: 'safeSet-many-catch-all',
        /**
         * It's hard to reason about which capabilities will actually be affected in this
         * circumstance so we'll assume C2
         */
        capabilityTier: CapabilityTier.C2,
        customerImpact: CustomerImpact.CUSTOMERS,
      },
    });
    return;
  }
}

type ClearParams = Parameters<typeof clear>;
export async function safeClear(...args: ClearParams): Promise<void> {
  try {
    return await withIndexeddbTimeout(() => clear(...args));
  } catch (error) {
    captureException(error, {
      tags: {
        captureLocation: 'safeClear-catch-all',
        /**
         * It's hard to reason about which capabilities will actually be affected in this
         * circumstance so we'll assume C2
         */
        capabilityTier: CapabilityTier.C2,
        customerImpact: CustomerImpact.CUSTOMERS,
      },
    });
  }
}

type CreateStoreParams = Parameters<typeof createStore>;
export function safeCreateStore(...args: CreateStoreParams): UseStore | undefined {
  try {
    return createStore(...args);
  } catch (error) {
    captureException(error, {
      tags: {
        captureLocation: 'safeCreateStore-catch-all',
        /**
         * It's hard to reason about which capabilities will actually be affected in this
         * circumstance so we'll assume C2
         */
        capabilityTier: CapabilityTier.C2,
        customerImpact: CustomerImpact.CUSTOMERS,
      },
    });
  }
}
